<script lang="ts">
  import SearchIcon from "../utils/assets/search_icon.svelte";
  import UnionIcon from "../utils/assets/union_icon.svelte";
  import { onMount } from "svelte";
  import { selected_language, setLanguage } from "../stores/chat";
  import ChevronDownIcon from "../utils/assets/chevron_down.svelte";
  import { client_config } from "../stores/store";
  import type { Language } from "../types/globalTypes";

  interface Languages {
    popular_languages: Language[];
    other_languages: Language[];
  }

  export let languages: Languages;

  let isOpen = false;
  let dropdownContainer: HTMLElement;
  let searchQuery = "";

  $: filteredLanguages = [
    ...languages?.popular_languages,
    ...languages?.other_languages,
  ].filter((lang) => {
    if (!lang || !lang?.name) return false;
    return lang?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase());
  });

  function handleClickOutside(event: MouseEvent) {
    if (
      dropdownContainer &&
      !dropdownContainer?.contains(event?.target as Node)
    ) {
      isOpen = false;
    }
  }

  function toggleDropdown() {
    isOpen = !isOpen;
  }

  function selectLanguage(language: Language) {
    setLanguage(language);
    isOpen = false;
  }

  onMount(() => {
    document?.addEventListener("click", handleClickOutside);
    return () => {
      document?.removeEventListener("click", handleClickOutside);
    };
  });
</script>

<div
  bind:this={dropdownContainer}
  class="verbaflo-widget-language-selection-dropdown-container"
>
  <button
    on:click={toggleDropdown}
    class="verbaflo-widget-selected-language-container"
  >
    <span class="verbaflo-widget-language-icon">
      <UnionIcon color="white" size="14" />
    </span>
    <span class="verbaflo-widget-selected-language-text"
      >{$selected_language?.name?.slice(0, 3)}</span
    >
    <span class="verbaflo-widget-language-icon">
      <ChevronDownIcon color="white" size="14" />
    </span>
  </button>
  {#if isOpen}
    <div class="verbaflo-widget-language-options-container">
      <div class="verbaflo-widget-language-search-parent-container">
        <div class="verbaflo-widget-language-search-container">
          <SearchIcon />
          <input
            bind:value={searchQuery}
            placeholder={$client_config?.language_dropdown_texts?.placeholder ||
              "Search Language"}
            class="verbaflo-widget-language-search-input"
          />
        </div>
      </div>

      <div class="verbaflo-widget-language-options-list-container">
        {#if searchQuery === ""}
          <div class="verbaflo-widget-language-options-list-item-container">
            <p class="verbaflo-widget-language-options-list-item-title">
              {$client_config?.language_dropdown_texts?.popular_label ||
                "Popular Languages"}
            </p>
            <div
              class="verbaflo-widget-language-options-list-item-options-container"
            >
              {#each languages?.popular_languages as language}
                <button
                  on:click={() => selectLanguage(language)}
                  class:selected={$selected_language?.name === language?.name}
                >
                  {language?.name}
                </button>
              {/each}
            </div>
          </div>
          <div class="verbaflo-widget-language-options-list-item-container">
            <p class="verbaflo-widget-language-options-list-item-title">
              {$client_config?.language_dropdown_texts?.all_label ||
                "All Languages"}
            </p>
            <div
              class="verbaflo-widget-language-options-list-item-options-container"
            >
              {#each languages?.other_languages as language}
                <button
                  on:click={() => selectLanguage(language)}
                  class:selected={$selected_language?.name === language?.name}
                >
                  {language?.name}
                </button>
              {/each}
            </div>
          </div>
        {:else}
          <div class="verbaflo-widget-language-options-list-item-container">
            <p class="verbaflo-widget-language-options-list-item-title">
              {$client_config?.language_dropdown_texts?.results_label ||
                "Search Results"}
            </p>
            <div
              class="verbaflo-widget-language-options-list-item-options-container"
            >
              {#each filteredLanguages as language}
                <button
                  on:click={() => selectLanguage(language)}
                  class:selected={$selected_language?.name === language?.name}
                >
                  {language?.name}
                </button>
              {/each}
            </div>
          </div>
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  :global(#vfwidget .verbaflo-widget-language-selection-dropdown-container) {
    position: relative !important;
  }

  :global(#vfwidget .verbaflo-widget-selected-language-container) {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 4px !important;
    cursor: pointer !important;
    opacity: 1 !important;
    background-color: transparent !important;
    border: none !important;
    padding: 0px !important;
    margin: 0px !important;
    outline: none !important;
    box-shadow: none !important;
    border: none !important;
    padding: 4px !important;
    color: #ffffff !important;
  }

  :global(#vfwidget .verbaflo-widget-selected-language-container:hover) {
    text-decoration: none !important;
  }

  :global(#vfwidget .verbaflo-widget-selected-language-container:active) {
    text-decoration: none !important;
  }

  :global(
      #vfwidget
        .verbaflo-widget-language-options-list-item-options-container
        button.selected
    ) {
    background-color: #f3f4f6 !important;
    border-radius: 4px !important;
  }

  :global(#vfwidget .verbaflo-widget-language-icon) {
    width: 14px !important;
    height: 14px !important;
    min-width: 14px !important;
    min-height: 14px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    color: #ffffff !important;
    flex-shrink: 0 !important;
  }

  :global(#vfwidget .verbaflo-widget-language-icon svg) {
    width: 14px !important;
    height: 14px !important;
    display: block !important;
  }

  :global(#vfwidget .verbaflo-widget-selected-language-text) {
    font-size: 12px !important;
    font-weight: 500 !important;
    color: #ffffff !important;
    text-transform: uppercase !important;
    margin: 0px !important;
    padding: 0px !important;
    line-height: normal !important;
  }

  :global(#vfwidget .verbaflo-widget-language-options-container) {
    position: absolute !important;
    top: 25px !important;
    right: 0px !important;
    z-index: 1000 !important;
    background-color: #ffffff !important;
    border: 1px solid #ededed !important;
    border-radius: 4px !important;
    box-shadow:
      0px 0px 4px rgba(0, 0, 0, 0.04),
      0px 4px 8px rgba(0, 0, 0, 0.06) !important;
    width: 160px !important;
  }

  :global(#vfwidget .verbaflo-widget-language-search-parent-container) {
    background: #ffffff !important;
    box-shadow:
      0px 0px 4px rgba(0, 0, 0, 0.04),
      0px 8px 16px rgba(0, 0, 0, 0.08) !important;
    border-radius: 4px !important;
    padding: 8px !important;
    margin-bottom: 1px !important;
  }

  :global(#vfwidget .verbaflo-widget-language-search-container) {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    background: #ffffff !important;
    border: 1px solid #d0d5dd !important;
    border-radius: 8px !important;
    padding: 8px !important;
  }

  :global(#vfwidget .verbaflo-widget-language-search-input) {
    width: 100% !important;
    border: none !important;
    outline: none !important;
    border: none !important;
    background: transparent !important;
    font-size: 12px !important;
    font-weight: 400 !important;
    color: #717680 !important;
    margin: 0px !important;
    padding: 0px !important;
    line-height: normal !important;
  }

  :global(#vfwidget .verbaflo-widget-language-options-list-container) {
    min-height: 50px !important;
    max-height: 200px !important;
    overflow-y: auto !important;
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
    padding: 8px !important;
  }

  :global(
      #vfwidget
        .verbaflo-widget-language-options-list-container::-webkit-scrollbar
    ) {
    display: none !important;
  }

  :global(#vfwidget .verbaflo-widget-language-options-list-item-title) {
    font-size: 12px !important;
    font-weight: 500 !important;
    color: #a4a7ae !important;
    text-transform: none !important;
    margin-top: 4px !important;
    padding: 0px !important;
    line-height: normal !important;
  }

  :global(
      #vfwidget .verbaflo-widget-language-options-list-item-options-container
    ) {
    display: flex !important;
    flex-direction: column !important;
  }

  :global(
      #vfwidget
        .verbaflo-widget-language-options-list-item-options-container
        button
    ) {
    font-size: 14px !important;
    font-weight: 400 !important;
    color: #181d27 !important;
    margin: 0px !important;
    margin-top: 3px !important;
    margin-bottom: 3px !important;
    padding: 0px !important;
    line-height: normal !important;
    text-transform: capitalize !important;
    cursor: pointer !important;
    padding: 6px 4px !important;
    opacity: 1 !important;
    background-color: transparent !important;
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    border-radius: 0px !important;
    text-align: left !important;
  }

  :global(
      #vfwidget
        .verbaflo-widget-language-options-list-item-options-container
        button:hover
    ) {
    background-color: #eef4ff !important;
    border-radius: 4px !important;
    padding: 6px 4px !important;
    text-decoration: none !important;
  }

  :global(
      #vfwidget
        .verbaflo-widget-language-options-list-item-options-container
        button:active
    ) {
    text-decoration: none !important;
  }
</style>
